import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import * as trainersApi from '../../api/trainers'
import * as linksApi from '../../api/links'
import { useUIStore } from '../../stores/uiStore'
import Card from '../../components/common/Card'
import Button from '../../components/common/Button'
import Badge from '../../components/common/Badge'
import Spinner from '../../components/common/Spinner'
import LinkTeamModal from '../../components/teams/LinkTeamModal'
import {
  UserCircleIcon,
  EnvelopeIcon,
  PhoneIcon,
  DocumentTextIcon,
  UserGroupIcon,
  TrashIcon,
  PencilIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'

/**
 * Trainer Detail Page
 * View and manage trainer information with tabs
 */
export default function TrainerDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { showToast } = useUIStore()
  
  const [activeTab, setActiveTab] = useState('overview')

  // Fetch full trainer details
  const { data: trainer, isLoading } = useQuery({
    queryKey: ['trainer', id, 'full'],
    queryFn: () => trainersApi.getTrainerFull(id),
  })

  // Fetch trainer's contracts
  const { data: contracts } = useQuery({
    queryKey: ['trainer', id, 'contracts'],
    queryFn: () => trainersApi.getTrainerContracts(id),
    enabled: activeTab === 'contracts',
  })

  // Fetch trainer's teams
  const { data: teams } = useQuery({
    queryKey: ['trainer', id, 'teams'],
    queryFn: () => trainersApi.getTrainerTeams(id),
    enabled: activeTab === 'teams',
  })

  // Delete trainer mutation
  const deleteMutation = useMutation({
    mutationFn: () => trainersApi.deleteTrainer(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['trainers'])
      queryClient.invalidateQueries(['dashboard-stats'])
      showToast('Trainer deleted successfully', 'success')
      navigate('/trainers')
    },
    onError: (error) => {
      showToast(error.message || 'Failed to delete trainer', 'error')
    },
  })

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this trainer? This action cannot be undone.')) {
      deleteMutation.mutate()
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Spinner size="lg" />
      </div>
    )
  }

  if (!trainer) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Trainer not found</p>
        <Button 
          variant="primary" 
          onClick={() => navigate('/trainers')}
          className="mt-4"
        >
          Back to Trainers
        </Button>
      </div>
    )
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: UserCircleIcon },
    { id: 'contracts', label: 'Contracts', icon: DocumentTextIcon },
    { id: 'teams', label: 'Teams', icon: UserGroupIcon },
  ]

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
            <UserCircleIcon className="h-10 w-10 text-primary-600" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {trainer.name}
            </h1>
            <p className="text-gray-600">{trainer.uid}</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate(`/trainers/${id}/edit`)}
          >
            <PencilIcon className="h-4 w-4 mr-1" />
            Edit
          </Button>
          
          <Button
            variant="danger"
            size="sm"
            onClick={handleDelete}
            loading={deleteMutation.isPending}
          >
            <TrashIcon className="h-4 w-4 mr-1" />
            Delete
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-8">
          {tabs.map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 pb-4 border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-5 w-5" />
                <span className="font-medium">{tab.label}</span>
              </button>
            )
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="pb-8">
        {activeTab === 'overview' && <OverviewTab trainer={trainer} />}
        {activeTab === 'contracts' && <ContractsTab contracts={contracts} />}
        {activeTab === 'teams' && <TeamsTab teams={teams} trainerId={id} />}
      </div>

      {/* Back Button */}
      <div className="flex justify-start">
        <Button variant="secondary" onClick={() => navigate('/trainers')}>
          Back to Trainers
        </Button>
      </div>
    </div>
  )
}

// Overview Tab Component
function OverviewTab({ trainer }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Personal Information */}
      <Card>
        <Card.Header>
          <h2 className="text-lg font-semibold text-gray-900">Personal Information</h2>
        </Card.Header>
        <Card.Body>
          <dl className="space-y-4">
            <div>
              <dt className="text-sm font-medium text-gray-500">Full Name</dt>
              <dd className="mt-1 text-sm text-gray-900">{trainer.name}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Trainer ID</dt>
              <dd className="mt-1 text-sm text-gray-900">{trainer.uid}</dd>
            </div>
            {trainer.email && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Email</dt>
                <dd className="mt-1 flex items-center gap-2 text-sm text-gray-900">
                  <EnvelopeIcon className="h-4 w-4 text-gray-400" />
                  <a href={`mailto:${trainer.email}`} className="text-primary-600 hover:text-primary-700">
                    {trainer.email}
                  </a>
                </dd>
              </div>
            )}
            {trainer.iban && (
              <div>
                <dt className="text-sm font-medium text-gray-500">IBAN</dt>
                <dd className="mt-1 text-sm text-gray-900">{trainer.iban}</dd>
              </div>
            )}
            {trainer.trainer_tennaamstelling && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Tennaamstelling</dt>
                <dd className="mt-1 text-sm text-gray-900">{trainer.trainer_tennaamstelling}</dd>
              </div>
            )}
            {trainer.phone && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Phone</dt>
                <dd className="mt-1 flex items-center gap-2 text-sm text-gray-900">
                  <PhoneIcon className="h-4 w-4 text-gray-400" />
                  <a href={`tel:${trainer.phone}`} className="text-primary-600 hover:text-primary-700">
                    {trainer.phone}
                  </a>
                </dd>
              </div>
            )}
          </dl>
        </Card.Body>
      </Card>

      {/* Statistics */}
      <Card>
        <Card.Header>
          <h2 className="text-lg font-semibold text-gray-900">Statistics</h2>
        </Card.Header>
        <Card.Body>
          <dl className="space-y-4">
            <div>
              <dt className="text-sm font-medium text-gray-500">Total Contracts</dt>
              <dd className="mt-1 text-2xl font-bold text-gray-900">
                {trainer.contracts?.length || 0}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Total Teams</dt>
              <dd className="mt-1 text-2xl font-bold text-gray-900">
                {trainer.teams?.length || 0}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Member Since</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {new Date(trainer.created_at).toLocaleDateString()}
              </dd>
            </div>
          </dl>
        </Card.Body>
      </Card>
    </div>
  )
}

// Contracts Tab Component
function ContractsTab({ contracts }) {
  const navigate = useNavigate()

  return (
    <Card>
      <Card.Header>
        <h2 className="text-lg font-semibold text-gray-900">
          Contracts ({contracts?.length || 0})
        </h2>
      </Card.Header>
      <Card.Body>
        {contracts && contracts.length > 0 ? (
          <div className="space-y-3">
            {contracts.map((contract) => (
              <div 
                key={contract.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                onClick={() => navigate(`/contracts/${contract.id}`)}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <DocumentTextIcon className="h-5 w-5 text-primary-600" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{contract.title}</p>
                      <p className="text-xs text-gray-500">{contract.uid}</p>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                    <span>
                      {new Date(contract.start_date).toLocaleDateString()} - {new Date(contract.end_date).toLocaleDateString()}
                    </span>
                    {contract.total_amount && (
                      <span className="font-medium">€{contract.total_amount.toLocaleString()}</span>
                    )}
                  </div>
                </div>
                <Badge variant={contract.status === 'signed' ? 'success' : 'warning'}>
                  {contract.status}
                </Badge>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 text-center py-8">
            No contracts found for this trainer
          </p>
        )}
      </Card.Body>
    </Card>
  )
}

// Teams Tab Component
function TeamsTab({ teams, trainerId }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { showToast } = useUIStore()
  const [showLinkTeamModal, setShowLinkTeamModal] = useState(false)

  // Unlink team mutation
  const unlinkTeamMutation = useMutation({
    mutationFn: (teamId) => linksApi.unlinkTrainerTeam(trainerId, teamId),
    onSuccess: () => {
      queryClient.invalidateQueries(['trainer', trainerId])
      queryClient.invalidateQueries(['teams'])
      showToast('Team unlinked successfully', 'success')
    },
    onError: (error) => {
      showToast(error.response?.data?.error || 'Failed to unlink team', 'error')
    },
  })

  const handleUnlinkTeam = (teamId, teamName) => {
    if (window.confirm(`Are you sure you want to unlink ${teamName}?`)) {
      unlinkTeamMutation.mutate(teamId)
    }
  }

  // Get trainer data for the modal
  const { data: trainer } = useQuery({
    queryKey: ['trainer', trainerId, 'full'],
    queryFn: () => trainersApi.getTrainerFull(trainerId),
  })

  return (
    <>
      <Card>
        <Card.Header>
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Teams ({teams?.length || 0})
            </h2>
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowLinkTeamModal(true)}
            >
              <UserGroupIcon className="h-4 w-4 mr-1" />
              Link Team
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {teams && teams.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {teams.map((team) => (
                <div
                  key={team.id}
                  className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors group"
                >
                  <UserGroupIcon className="h-6 w-6 text-blue-600 mt-0.5" />
                  <div
                    className="flex-1 cursor-pointer"
                    onClick={() => navigate(`/teams/${team.id}`)}
                  >
                    <p className="text-sm font-medium text-gray-900">{team.team_name}</p>
                    <p className="text-xs text-gray-500">{team.uid}</p>
                    {team.category && (
                      <p className="text-xs text-gray-500 mt-1">Category: {team.category}</p>
                    )}
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleUnlinkTeam(team.id, team.team_name)
                    }}
                    className="text-red-600 hover:text-red-700 transition-colors opacity-0 group-hover:opacity-100"
                    title="Unlink team"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-sm text-gray-500 mb-3">
                No teams found for this trainer
              </p>
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowLinkTeamModal(true)}
              >
                <UserGroupIcon className="h-4 w-4 mr-1" />
                Link Team
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>

      {trainer && (
        <LinkTeamModal
          trainer={trainer}
          isOpen={showLinkTeamModal}
          onClose={() => setShowLinkTeamModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries(['trainer', trainerId])
            queryClient.invalidateQueries(['teams'])
          }}
        />
      )}
    </>
  )
}